import { useEffect, useMemo, useState } from 'react';
import { authClient } from '../utils/amplifyClient';
import '../css/TireModuleContent.css';

import * as Sentry from '@sentry/react';

import { formatDateDisplay } from '../utils/helperFunctions';

import { useUserContext } from '../contexts/UserContext';

import {
    deleteTire,
    createTire,
    updateTire,
} from '../graphql/mutations';
import {
    listTireByCompanyId as listTireByCompanyIdQuery,
    getAxleBlueprint,
} from '../graphql/queries';
import { Tire, SingleUnit, CreateTireInput } from '../types/schema';
import { UpdateTireInput } from '../graphql/API';

import { useEquipmentContext } from '../contexts/EquipmentContext';
import { CustomModal } from './CustomModal';
import { TIRE_CATEGORIES } from '../utils/constants';
import type { AxleBlueprint } from '../types/axleBlueprint';
import { SelectWithCustom } from './SelectWithCustom';
import {
    Btn,
    BtnIconOnly,
    IconCheckCircle,
    IconPlusCircle,
    IconSearch,
    IconTrash,
    PageHeader,
    StatsGrid,
    Stack,
} from './ui';
import { IconAlertTriangle, IconQuestionCircle, IconLinkBroken, IconTires } from '@icons';

type TabId = 'inventory' | 'installed' | 'removed';

interface TireWithUnit extends Tire {
    unitName?: string;
}

// TODO: on the assign tab, potentially only show slots that have the same category type as the tire that I'm trying to assign, simplify the list and make it harder to accidentally assign the wrong type of tire to the wrong asset.

export const TireModuleContent = () => {
    const [allTires, setAllTires] = useState<Tire[]>([]);
    const [isLoadingTires, setIsLoadingTires] = useState(false);
    const [activeTab, setActiveTab] = useState<TabId>('removed');
    const [sortField, setSortField] = useState<string>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [tireToDelete, setTireToDelete] = useState<Tire | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showAddTireModal, setShowAddTireModal] = useState(false);

    // Remove tire (unassign from unit → back to inventory)
    const [tireToUnassign, setTireToUnassign] = useState<Tire | null>(null);
    const [isUnassigning, setIsUnassigning] = useState(false);

    // Remove from service
    const [tireToRetire, setTireToRetire] = useState<Tire | null>(null);
    const [isRetiring, setIsRetiring] = useState(false);
    const [retireReason, setRetireReason] = useState('');
    const [newTire, setNewTire] = useState({
        name: '',
        tireManufacturer: '',
        tireCategory: '',
        tireModel: '',
        tireSize: '',
        loadRating: '',
        loadRatingUnit: 'lbs',
        treadDepthAtInstall: '',
        tireStatus: 'New',
        cost: '',
    });
    const [isSavingTire, setIsSavingTire] = useState(false);

    // Assign tire modal state
    const [tireToAssign, setTireToAssign] = useState<Tire | null>(null);
    const [assignUnitId, setAssignUnitId] = useState('');
    const [assignBlueprint, setAssignBlueprint] = useState<AxleBlueprint | null>(null);
    const [assignSlotId, setAssignSlotId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isLoadingBlueprint, setIsLoadingBlueprint] = useState(false);
    const [assignUnitTires, setAssignUnitTires] = useState<Tire[]>([]);
    const [assignWorkingPSI, setAssignWorkingPSI] = useState('');
    const [assignInflationMedium, setAssignInflationMedium] = useState('');
    const [assignCurrentTread, setAssignCurrentTread] = useState('');
    const [assignMinTread, setAssignMinTread] = useState('');
    const [assignTreadNotify, setAssignTreadNotify] = useState(false);

    const { company, companyOwner } = useUserContext();
    const { companyGroupedEquipmentData, refreshEquipment } = useEquipmentContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;

    // Fetch all tires for this company
    const fetchTires = async () => {
        if (!companyId) return;
        setIsLoadingTires(true);
        try {
            const result = await authClient.graphql({
                query: listTireByCompanyIdQuery,
                variables: { companyId },
            });
            setAllTires((result.data.listTireByCompanyId?.items?.filter(Boolean) as Tire[]) || []);
        } catch (error) {
            console.error('Error fetching tires:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoadingTires(false);
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchTires();
            refreshEquipment();
        }
    }, [companyId]);

    // Build a map of unitId -> unit name for display
    const unitNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        companyGroupedEquipmentData.forEach((category) => {
            category.units?.forEach((unit: SingleUnit | null) => {
                if (unit) {
                    map[unit.id] = unit.name;
                }
            });
        });
        return map;
    }, [companyGroupedEquipmentData]);

    // Enrich tires with unit names
    const tiresWithUnits: TireWithUnit[] = useMemo(() => {
        return allTires.map((tire) => ({
            ...tire,
            unitName: tire.unitId ? unitNameMap[tire.unitId] : undefined,
        }));
    }, [allTires, unitNameMap]);

    // Filter tires by tab
    const inventoryTires = useMemo(
        () => tiresWithUnits.filter((t) => !t.unitId && !t.removedFromService),
        [tiresWithUnits],
    );

    const installedTires = useMemo(
        () => tiresWithUnits.filter((t) => !!t.unitId && !t.removedFromService),
        [tiresWithUnits],
    );

    const removedTires = useMemo(() => tiresWithUnits.filter((t) => t.removedFromService === true), [tiresWithUnits]);

    const currentTires = useMemo(() => {
        switch (activeTab) {
            case 'inventory':
                return inventoryTires;
            case 'installed':
                return installedTires;
            case 'removed':
                return removedTires;
            default:
                return [];
        }
    }, [activeTab, inventoryTires, installedTires, removedTires]);

    // Unique tire sizes for the selected category (for the add tire dropdown)
    const tireSizesForCategory = useMemo(() => {
        if (!newTire.tireCategory) return [];
        const sizes = allTires
            .filter((t) => t.tireCategory === newTire.tireCategory && t.tireSize)
            .map((t) => t.tireSize as string);
        return [...new Set(sizes)].sort();
    }, [allTires, newTire.tireCategory]);

    // Unique tire models for the selected manufacturer
    const tireModelsForManufacturer = useMemo(() => {
        if (!newTire.tireManufacturer) return [];
        const models = allTires
            .filter((t) => t.tireManufacturer === newTire.tireManufacturer && t.tireModel)
            .map((t) => t.tireModel as string);
        return [...new Set(models)].sort();
    }, [allTires, newTire.tireManufacturer]);

    // Sort tires
    const sortedTires = useMemo(() => {
        const sorted = [...currentTires];
        sorted.sort((a, b) => {
            let aVal = '';
            let bVal = '';
            switch (sortField) {
                case 'name':
                    aVal = a.name || '';
                    bVal = b.name || '';
                    break;
                case 'unitName':
                    aVal = a.unitName || '';
                    bVal = b.unitName || '';
                    break;
                case 'failureReason':
                    aVal = a.failureReason || '';
                    bVal = b.failureReason || '';
                    break;
                case 'removedFromServiceDate':
                    aVal = a.removedFromServiceDate || '';
                    bVal = b.removedFromServiceDate || '';
                    break;
                case 'dateInstalled':
                    aVal = a.dateInstalled || '';
                    bVal = b.dateInstalled || '';
                    break;
                default:
                    aVal = a.name || '';
                    bVal = b.name || '';
            }
            const cmp = aVal.localeCompare(bVal);
            return sortDirection === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [currentTires, sortField, sortDirection]);

    // Stats
    const activeTiresCount = installedTires.length;
    const unassignedTiresCount = inventoryTires.length;
    const tiresBelowThreshold = useMemo(
        () =>
            allTires.filter((t) => {
                if (t.currentTreadDepth == null || t.minimumTreadDepth == null) return false;
                return t.currentTreadDepth <= t.minimumTreadDepth;
            }).length,
        [allTires],
    );
    const missingAssignments = useMemo(
        () => allTires.filter((t) => !t.unitId && !t.removedFromService && !t.assignedAxle).length,
        [allTires],
    );

    // Tire Health Overview data for donut chart
    const healthData = useMemo(() => {
        const activeTires = allTires.filter((t) => !t.removedFromService);
        let healthy = 0,
            monitor = 0,
            urgent = 0,
            critical = 0;

        activeTires.forEach((t) => {
            if (t.currentTreadDepth == null || t.treadDepthAtInstall == null || t.treadDepthAtInstall === 0) {
                healthy++; // default to healthy if no data
                return;
            }
            const pct = (t.currentTreadDepth / t.treadDepthAtInstall) * 100;
            if (pct >= 50) healthy++;
            else if (pct >= 25) monitor++;
            else if (pct >= 10) urgent++;
            else critical++;
        });

        return { healthy, monitor, urgent, critical, total: activeTires.length };
    }, [allTires]);

    // Upcoming replacements - tires with lowest remaining tread %
    const upcomingReplacements = useMemo(() => {
        return allTires
            .filter(
                (t) =>
                    !t.removedFromService &&
                    t.currentTreadDepth != null &&
                    t.treadDepthAtInstall != null &&
                    t.treadDepthAtInstall > 0,
            )
            .map((t) => {
                const pct = Math.round(((t.currentTreadDepth ?? 0) / (t.treadDepthAtInstall ?? 1)) * 100);
                return { ...t, remainingPct: pct, unitName: t.unitId ? unitNameMap[t.unitId] : undefined };
            })
            .sort((a, b) => a.remainingPct - b.remainingPct)
            .slice(0, 4);
    }, [allTires, unitNameMap]);

    const getHealthBadgeClass = (pct: number) => {
        if (pct < 10) return 'critical';
        if (pct < 25) return 'urgent';
        if (pct < 50) return 'monitor';
        return 'healthy';
    };

    // Donut chart SVG
    const renderDonutChart = () => {
        const { healthy, monitor, urgent, critical, total } = healthData;
        if (total === 0)
            return <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No tire data</div>;

        const segments = [
            { value: healthy, color: '#22c55e' },
            { value: monitor, color: '#eab308' },
            { value: urgent, color: '#f97316' },
            { value: critical, color: '#dc2626' },
        ];

        const radius = 80;
        const strokeWidth = 30;
        const circumference = 2 * Math.PI * radius;
        let offset = 0;

        return (
            <svg viewBox="0 0 200 200" width="180" height="180">
                {segments.map((seg, i) => {
                    const pct = seg.value / total;
                    const dashLength = pct * circumference;
                    const dashOffset = -offset;
                    offset += dashLength;
                    if (seg.value === 0) return null;
                    return (
                        <circle
                            key={i}
                            cx="100"
                            cy="100"
                            r={radius}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                            strokeDashoffset={dashOffset}
                            transform="rotate(-90 100 100)"
                        />
                    );
                })}
                <text x="100" y="95" textAnchor="middle" fontSize="28" fontWeight="700" fill="#1a365d">
                    {total}
                </text>
                <text x="100" y="115" textAnchor="middle" fontSize="12" fill="#64748b">
                    Active
                </text>
            </svg>
        );
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleDeleteTire = async () => {
        if (!tireToDelete?.id) return;
        setIsDeleting(true);
        try {
            await authClient.graphql({
                query: deleteTire,
                variables: { input: { id: tireToDelete.id } },
            });
            setAllTires((prev) => prev.filter((t) => t.id !== tireToDelete.id));
            setTireToDelete(null);
        } catch (error) {
            console.error('Error deleting tire:', error);
            Sentry.captureException(error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Unassign tire from unit → back to inventory (keeps tread/pressure data)
    const handleUnassignTire = async () => {
        if (!tireToUnassign?.id) return;
        setIsUnassigning(true);
        try {
            await authClient.graphql({
                query: updateTire,
                variables: {
                    // `singleUnitTiresId` is a residual Gen1 field not present on the Gen2
                    // UpdateTireInput; preserve it in the payload via a typed intersection cast.
                    input: {
                        id: tireToUnassign.id,
                        unitId: null,
                        assignedPosition: null,
                        assignedAxle: null,
                        dateInstalled: null,
                        singleUnitTiresId: null,
                    } as UpdateTireInput & { singleUnitTiresId?: string | null },
                },
            });
            setAllTires((prev) =>
                prev.map((t) =>
                    t.id === tireToUnassign.id
                        ? { ...t, unitId: null, assignedPosition: null, assignedAxle: null, dateInstalled: null }
                        : t,
                ),
            );
            setTireToUnassign(null);
        } catch (error) {
            console.error('Error unassigning tire:', error);
            Sentry.captureException(error);
        } finally {
            setIsUnassigning(false);
        }
    };

    // Remove tire from service entirely
    const handleRetireTire = async () => {
        if (!tireToRetire?.id) return;
        setIsRetiring(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            await authClient.graphql({
                query: updateTire,
                variables: {
                    input: {
                        id: tireToRetire.id,
                        removedFromService: true,
                        removedFromServiceDate: today,
                        failureReason: retireReason || null,
                    },
                },
            });
            setAllTires((prev) =>
                prev.map((t) =>
                    t.id === tireToRetire.id
                        ? {
                              ...t,
                              removedFromService: true,
                              removedFromServiceDate: today,
                              failureReason: retireReason || null,
                          }
                        : t,
                ),
            );
            setTireToRetire(null);
            setRetireReason('');
        } catch (error) {
            console.error('Error retiring tire:', error);
            Sentry.captureException(error);
        } finally {
            setIsRetiring(false);
        }
    };

    const handleCreateTire = async () => {
        if (!newTire.name.trim() || !newTire.tireSize.trim() || !newTire.tireStatus.trim() || !companyId) return;
        setIsSavingTire(true);
        try {
            const treadDepth = newTire.treadDepthAtInstall ? parseFloat(newTire.treadDepthAtInstall) : null;
            const loadRatingVal = newTire.loadRating ? parseFloat(newTire.loadRating) : null;
            const input: CreateTireInput = {
                name: newTire.name.trim(),
                companyId,
                tireManufacturer: newTire.tireManufacturer || null,
                tireCategory: newTire.tireCategory || null,
                tireModel: newTire.tireModel || null,
                tireSize: newTire.tireSize.trim(),
                tireStatus: newTire.tireStatus,
                treadDepthAtInstall: treadDepth,
                currentTreadDepth: treadDepth,
                removedFromService: false,
                loadRating: loadRatingVal,
                loadRatingUnit: newTire.loadRatingUnit || null,
            };
            const result = await authClient.graphql({
                query: createTire,
                variables: { input },
            });
            const created = result.data.createTire;
            if (created) {
                setAllTires((prev) => [...prev, created as Tire]);
            }
            setNewTire({
                name: '',
                tireManufacturer: '',
                tireCategory: '',
                tireModel: '',
                tireSize: '',
                loadRating: '',
                loadRatingUnit: 'lbs',
                treadDepthAtInstall: '',
                tireStatus: 'New',
                cost: '',
            });
            setShowAddTireModal(false);
        } catch (error) {
            console.error('Error creating tire:', error);
            Sentry.captureException(error);
        } finally {
            setIsSavingTire(false);
        }
    };

    // ── Units with blueprints for assign dropdown (category name + unit name) ──
    const unitsWithBlueprints = useMemo(() => {
        const result: { id: string; label: string; axleConfiguration: string }[] = [];
        companyGroupedEquipmentData.forEach((category) => {
            const catName = category.name || 'Uncategorized';
            category.units?.forEach((unit: SingleUnit | null) => {
                if (unit?.axleConfiguration) {
                    result.push({
                        id: unit.id,
                        label: `${catName} — ${unit.name}`,
                        axleConfiguration: unit.axleConfiguration,
                    });
                }
            });
        });
        return result;
    }, [companyGroupedEquipmentData]);

    // ── Fetch blueprint when unit is selected in assign modal ──
    const handleAssignUnitChange = async (unitId: string) => {
        setAssignUnitId(unitId);
        setAssignSlotId('');
        setAssignBlueprint(null);
        setAssignUnitTires([]);

        const unit = unitsWithBlueprints.find((u) => u.id === unitId);
        if (!unit?.axleConfiguration) return;

        // Get tires already installed on this unit
        const unitTires = allTires.filter((t) => t.unitId === unitId && !t.removedFromService);
        setAssignUnitTires(unitTires);

        setIsLoadingBlueprint(true);
        try {
            const result = await authClient.graphql({
                query: getAxleBlueprint,
                variables: { id: unit.axleConfiguration },
            });
            const bp = result.data.getAxleBlueprint;
            if (bp) {
                setAssignBlueprint({
                    id: bp.id,
                    name: bp.name,
                    companyId: bp.companyId,
                    centerlineLength: bp.centerlineLength,
                    axles: typeof bp.axles === 'string' ? JSON.parse(bp.axles) : bp.axles,
                });
            }
        } catch (err) {
            console.error('Error fetching blueprint for assign:', err);
        } finally {
            setIsLoadingBlueprint(false);
        }
    };

    // ── Assign tire to unit + slot ──
    const handleAssignTire = async () => {
        if (!tireToAssign || !assignUnitId) return;
        setIsAssigning(true);
        try {
            const selectedSlot = assignBlueprint?.axles
                .flatMap((a) => a.tireSlots.map((s) => ({ ...s, axleLabel: a.label })))
                .find((s) => s.id === assignSlotId);

            const currentTread = assignCurrentTread ? parseFloat(assignCurrentTread) : null;
            const minTread = assignMinTread ? parseFloat(assignMinTread) : null;
            const psi = assignWorkingPSI ? parseFloat(assignWorkingPSI) : null;

            await authClient.graphql({
                query: updateTire,
                variables: {
                    // `singleUnitTiresId` is a residual Gen1 field not present on the Gen2
                    // UpdateTireInput; preserve it in the payload via a typed intersection cast.
                    input: {
                        id: tireToAssign.id,
                        unitId: assignUnitId,
                        assignedPosition: selectedSlot?.positionCode || null,
                        assignedAxle: selectedSlot?.axleLabel || null,
                        dateInstalled: new Date().toISOString().split('T')[0],
                        singleUnitTiresId: assignUnitId,
                        currentTirePressure: psi,
                        inflationMedium: assignInflationMedium || null,
                        currentTreadDepth: currentTread,
                        minimumTreadDepth: minTread,
                    } as UpdateTireInput & { singleUnitTiresId?: string | null },
                },
            });

            // Update local state
            setAllTires((prev) =>
                prev.map((t) =>
                    t.id === tireToAssign.id
                        ? {
                              ...t,
                              unitId: assignUnitId,
                              assignedPosition: selectedSlot?.positionCode || null,
                              assignedAxle: selectedSlot?.axleLabel || null,
                              dateInstalled: new Date().toISOString().split('T')[0],
                              currentTirePressure: psi,
                              inflationMedium: assignInflationMedium || null,
                              currentTreadDepth: currentTread,
                              minimumTreadDepth: minTread,
                          }
                        : t,
                ),
            );

            setTireToAssign(null);
            setAssignUnitId('');
            setAssignBlueprint(null);
            setAssignSlotId('');
            setAssignWorkingPSI('');
            setAssignInflationMedium('');
            setAssignCurrentTread('');
            setAssignMinTread('');
            setAssignTreadNotify(false);
        } catch (error) {
            console.error('Error assigning tire:', error);
            Sentry.captureException(error);
        } finally {
            setIsAssigning(false);
        }
    };

    const tabs: { id: TabId; label: string; count: number }[] = [
        { id: 'inventory', label: 'Inventory', count: unassignedTiresCount },
        { id: 'installed', label: 'Installed', count: activeTiresCount },
        { id: 'removed', label: 'Removed from Service', count: removedTires.length },
    ];

    const getTireStatCards = (
        activeCount: number,
        unassignedCount: number,
        belowThresholdCount: number,
        missingCount: number,
    ) => [
        { icon: <IconTires />, value: activeCount, label: 'Active Tires' },
        { icon: <IconQuestionCircle />, value: unassignedCount, label: 'Unassigned Tires' },
        { icon: <IconAlertTriangle />, value: belowThresholdCount, label: 'Tires Below Threshold' },
        { icon: <IconLinkBroken />, value: missingCount, label: 'Missing Assignments' },
    ];

    // Table columns vary by tab
    const renderTableHeaders = () => {
        if (activeTab === 'removed') {
            return (
                <tr>
                    <th onClick={() => handleSort('name')}>
                        Tire ID <span className="sort-icon">⇅</span>
                    </th>
                    <th onClick={() => handleSort('unitName')}>
                        Asset <span className="sort-icon">⇅</span>
                    </th>
                    <th>Failure Reason</th>
                    <th onClick={() => handleSort('removedFromServiceDate')}>
                        Date Removed <span className="sort-icon">⇅</span>
                    </th>
                </tr>
            );
        }
        if (activeTab === 'installed') {
            return (
                <tr>
                    <th onClick={() => handleSort('name')}>
                        Tire ID <span className="sort-icon">⇅</span>
                    </th>
                    <th onClick={() => handleSort('unitName')}>
                        Asset <span className="sort-icon">⇅</span>
                    </th>
                    <th>Axle / Position</th>
                    <th onClick={() => handleSort('dateInstalled')}>
                        Date Installed <span className="sort-icon">⇅</span>
                    </th>
                    <th>Tread Depth</th>
                    <th>Action</th>
                </tr>
            );
        }
        // inventory
        return (
            <tr>
                <th onClick={() => handleSort('name')}>
                    Tire ID <span className="sort-icon">⇅</span>
                </th>
                <th>Manufacturer</th>
                <th>Model</th>
                <th>Size</th>
                <th>Status</th>
                <th>Action</th>
            </tr>
        );
    };

    const renderTableRow = (tire: TireWithUnit) => {
        if (activeTab === 'removed') {
            return (
                <tr key={tire.id}>
                    <td className="tire-id-cell">{tire.name || tire.id.slice(0, 8)}</td>
                    <td>{tire.unitName || '—'}</td>
                    <td>{tire.failureReason || '—'}</td>
                    <td>{tire.removedFromServiceDate ? formatDateDisplay(tire.removedFromServiceDate) : '—'}</td>
                </tr>
            );
        }
        if (activeTab === 'installed') {
            return (
                <tr key={tire.id}>
                    <td className="tire-id-cell">{tire.name || tire.id.slice(0, 8)}</td>
                    <td>{tire.unitName || '—'}</td>
                    <td>
                        {(() => {
                            const pos = tire.assignedPosition;
                            const axle = tire.assignedAxle;
                            // If position looks like a UUID, only show axle
                            const isUuid = pos && pos.length > 20;
                            const parts = [axle, isUuid ? null : pos].filter(Boolean);
                            return parts.length > 0 ? parts.join(' / ') : '—';
                        })()}
                    </td>
                    <td>{tire.dateInstalled ? formatDateDisplay(tire.dateInstalled) : '—'}</td>
                    <td>{tire.currentTreadDepth != null ? `${tire.currentTreadDepth}/32 in` : '—'}</td>
                    <td className="tire-action-cell">
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Remove"
                            buttonOnClick={() => setTireToUnassign(tire)}
                        />
                        <Btn
                            variant="danger-outlined"
                            buttonSize="sm"
                            buttonText="Retire"
                            buttonOnClick={() => setTireToRetire(tire)}
                        />
                    </td>
                </tr>
            );
        }
        // inventory
        return (
            <tr key={tire.id}>
                <td className="tire-id-cell">{tire.name || tire.id.slice(0, 8)}</td>
                <td>{tire.tireManufacturer || '—'}</td>
                <td>{tire.tireModel || '—'}</td>
                <td>{tire.tireSize || '—'}</td>
                <td>{tire.tireStatus || 'Available'}</td>
                <td className="tire-action-cell">
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonText="Assign Now"
                        buttonOnClick={() => setTireToAssign(tire)}
                    />
                    <Btn
                        variant="danger-outlined"
                        buttonSize="sm"
                        buttonText="Retire"
                        buttonOnClick={() => setTireToRetire(tire)}
                    />
                </td>
            </tr>
        );
    };

    return (
        <div className="page-container tire-module-container">
            {/* Header */}
            <PageHeader
                title="Tire Maintenance"
                actions={
                    <>
                        <Btn
                            variant="primary"
                            buttonText="Create Report"
                            ariaLabel="Create tire report"
                            buttonOnClick={() => alert('Create report clicked')}
                        />
                        <BtnIconOnly
                            size="sm"
                            ariaLabel="Search tires"
                            icon={<IconSearch />}
                            onClick={() => alert('Search clicked')}
                        />
                    </>
                }
            />

            {/* Stats grid */}
            <StatsGrid
                stats={getTireStatCards(
                    activeTiresCount,
                    unassignedTiresCount,
                    tiresBelowThreshold,
                    missingAssignments,
                ).map(({ icon, value, label }) => (
                    <div key={label} className="stat-item">
                        <div className="stat-row">
                            <div className="stat-icon">{icon}</div>
                            <div className="stat-value">{value}</div>
                        </div>
                        <div className="stat-row">
                            <div className="stat-label">{label}</div>
                        </div>
                    </div>
                ))}
            />

            {/* Action Buttons */}
            <div className="tire-action-buttons">
                <Btn
                    variant="secondary-outlined"
                    buttonText="Add Tire"
                    buttonIcon={<IconPlusCircle />}
                    buttonOnClick={() => setShowAddTireModal(true)}
                />
                <Btn
                    variant="primary"
                    buttonText="Record Tire Inspections"
                    buttonIcon={<IconCheckCircle />}
                    buttonOnClick={() => alert('Record inspection clicked')}
                />
                <Btn
                    variant="danger-outlined"
                    buttonText="Remove Tire"
                    buttonIcon={<IconTrash />}
                    buttonOnClick={() => alert('Remove tire clicked')}
                />
            </div>

            {/* Main Content: Table + Sidebar */}
            <div className="tire-main-content">
                {/* Left: Table Panel */}
                <div className="tire-table-panel">
                    <div className="tire-module-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`tire-module-tab ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                                {tab.id !== 'removed' &&
                                    ` (${tab.count} ${tab.id === 'inventory' ? 'Unassigned' : 'Active'})`}
                            </button>
                        ))}
                    </div>

                    {isLoadingTires ? (
                        <div className="tire-table-empty">Loading tires...</div>
                    ) : sortedTires.length === 0 ? (
                        <div className="tire-table-empty">No tires found for this view</div>
                    ) : (
                        <table className="tire-table">
                            <thead>{renderTableHeaders()}</thead>
                            <tbody>{sortedTires.map((tire) => renderTableRow(tire))}</tbody>
                        </table>
                    )}
                </div>

                {/* Right: Sidebar */}
                <div className="tire-sidebar">
                    {/* Tire Health Overview */}
                    <div className="tire-health-card">
                        <h3>Tire Health Overview</h3>
                        <div className="donut-chart-container">{renderDonutChart()}</div>
                        <div className="donut-legend">
                            <div className="donut-legend-item">
                                <span className="donut-legend-dot" style={{ background: '#22c55e' }} />
                                Healthy (≥50%)
                            </div>
                            <div className="donut-legend-item">
                                <span className="donut-legend-dot" style={{ background: '#eab308' }} />
                                Monitor (25–49%)
                            </div>
                            <div className="donut-legend-item">
                                <span className="donut-legend-dot" style={{ background: '#f97316' }} />
                                Urgent (10–24%)
                            </div>
                            <div className="donut-legend-item">
                                <span className="donut-legend-dot" style={{ background: '#dc2626' }} />
                                Critical (&lt;10%)
                            </div>
                        </div>
                    </div>

                    {/* Upcoming Replacements */}
                    <div className="tire-replacements-card">
                        <h3>Upcoming Replacements</h3>
                        <div className="replacements-header">
                            <span>Tire</span>
                            <span>% Remaining</span>
                        </div>
                        {upcomingReplacements.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '16px', fontSize: '13px' }}>
                                No replacement data available
                            </div>
                        ) : (
                            upcomingReplacements.map((tire) => (
                                <div key={tire.id} className="replacement-row">
                                    <span className="replacement-tire-name">
                                        {tire.name || tire.id.slice(0, 8)} – {tire.unitName || 'Unassigned'}
                                    </span>
                                    <span className={`replacement-badge ${getHealthBadgeClass(tire.remainingPct)}`}>
                                        {tire.remainingPct}%
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Add Tire Modal */}
            <CustomModal onDismiss={() => setShowAddTireModal(false)} visible={showAddTireModal} header="Add a Tire">
                <div className="add-tire-form">
                    <div className="add-tire-row full">
                        <div className="add-tire-field">
                            <label className="add-tire-label">
                                Tire ID <span className="required">*</span>
                            </label>
                            <input
                                className="add-tire-input"
                                placeholder="Enter Tire ID"
                                value={newTire.name}
                                onChange={(e) => setNewTire((prev) => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="add-tire-row">
                        <div className="add-tire-field">
                            <label className="add-tire-label">Manufacturer</label>
                            <SelectWithCustom
                                value={newTire.tireManufacturer}
                                onChange={(val) =>
                                    setNewTire((prev) => ({ ...prev, tireManufacturer: val, tireModel: '' }))
                                }
                                options={[
                                    'Michelin',
                                    'Bridgestone',
                                    'Goodyear',
                                    'Continental',
                                    'Firestone',
                                    'BFGoodrich',
                                    'Yokohama',
                                    'Toyo',
                                    'Cooper',
                                    'Hankook',
                                ]}
                                placeholder="Select Manufacturer"
                            />
                        </div>
                        <div className="add-tire-field">
                            <label className="add-tire-label">Model #</label>
                            <SelectWithCustom
                                value={newTire.tireModel}
                                onChange={(val) => setNewTire((prev) => ({ ...prev, tireModel: val }))}
                                options={tireModelsForManufacturer}
                                placeholder={
                                    newTire.tireManufacturer ? 'Select or add model...' : 'Select a manufacturer first'
                                }
                            />
                        </div>
                    </div>

                    <div className="add-tire-row">
                        <div className="add-tire-field">
                            <label className="add-tire-label">Category</label>
                            <select
                                className="add-tire-select"
                                value={newTire.tireCategory}
                                onChange={(e) =>
                                    setNewTire((prev) => ({ ...prev, tireCategory: e.target.value, tireSize: '' }))
                                }
                            >
                                <option value="">Select Category</option>
                                {Object.values(TIRE_CATEGORIES).map((cat) => (
                                    <option key={cat} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="add-tire-field">
                            <label className="add-tire-label">
                                Tire Size <span className="required">*</span>
                            </label>
                            <SelectWithCustom
                                value={newTire.tireSize}
                                onChange={(val) => setNewTire((prev) => ({ ...prev, tireSize: val }))}
                                options={tireSizesForCategory}
                                placeholder={newTire.tireCategory ? 'Select or add size...' : 'Select a category first'}
                            />
                        </div>
                    </div>

                    <div className="add-tire-row">
                        <div className="add-tire-field">
                            <label className="add-tire-label">Load Rating</label>
                            <div className="add-tire-input-with-unit">
                                <input
                                    className="add-tire-input"
                                    placeholder="e.g. 3500"
                                    type="number"
                                    min="0"
                                    value={newTire.loadRating}
                                    onChange={(e) => setNewTire((prev) => ({ ...prev, loadRating: e.target.value }))}
                                />
                                <select
                                    className="add-tire-unit-select"
                                    value={newTire.loadRatingUnit}
                                    onChange={(e) =>
                                        setNewTire((prev) => ({ ...prev, loadRatingUnit: e.target.value }))
                                    }
                                >
                                    <option value="lbs">lbs</option>
                                    <option value="kg">kg</option>
                                </select>
                            </div>
                        </div>
                        <div className="add-tire-field">
                            <label className="add-tire-label">Starting Tread Depth</label>
                            <div className="add-tire-input-with-unit">
                                <input
                                    className="add-tire-input"
                                    placeholder="e.g. 10"
                                    type="number"
                                    min="0"
                                    max="40"
                                    step="1"
                                    value={newTire.treadDepthAtInstall}
                                    onChange={(e) =>
                                        setNewTire((prev) => ({ ...prev, treadDepthAtInstall: e.target.value }))
                                    }
                                />
                                <span className="add-tire-unit">/32 in</span>
                            </div>
                        </div>
                    </div>

                    <div className="add-tire-row">
                        <div className="add-tire-field">
                            <label className="add-tire-label">
                                Tire Status <span className="required">*</span>
                            </label>
                            <select
                                className="add-tire-select"
                                value={newTire.tireStatus}
                                onChange={(e) => setNewTire((prev) => ({ ...prev, tireStatus: e.target.value }))}
                            >
                                <option value="New">New</option>
                                <option value="Used">Used</option>
                                <option value="Retread">Retread</option>
                            </select>
                        </div>
                        <div className="add-tire-field">
                            <label className="add-tire-label">Cost</label>
                            <input
                                className="add-tire-input"
                                placeholder="Enter Tire Cost"
                                type="number"
                                value={newTire.cost}
                                onChange={(e) => setNewTire((prev) => ({ ...prev, cost: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="add-tire-footer">
                        <Btn
                            variant="primary"
                            buttonText={isSavingTire ? 'Saving...' : 'Record'}
                            buttonDisabled={!newTire.name.trim() || !newTire.tireSize.trim() || isSavingTire}
                            buttonOnClick={handleCreateTire}
                        />
                    </div>
                </div>
            </CustomModal>

            {/* Assign Tire Modal */}
            <CustomModal
                onDismiss={() => {
                    setTireToAssign(null);
                    setAssignUnitId('');
                    setAssignBlueprint(null);
                    setAssignSlotId('');
                    setAssignUnitTires([]);
                    setAssignWorkingPSI('');
                    setAssignInflationMedium('');
                    setAssignCurrentTread('');
                    setAssignMinTread('');
                    setAssignTreadNotify(false);
                }}
                visible={!!tireToAssign}
                header="Assign a Tire"
                size="large"
            >
                <div className="assign-tire-form">
                    <div className="assign-top-row">
                        <div className="assign-left-col">
                            <div className="assign-inline-header">
                                <div className="assign-date-badge">
                                    📅{' '}
                                    {new Date().toLocaleDateString('en-US', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        year: '2-digit',
                                    })}
                                </div>
                                <div className="assign-inline-tire-id">
                                    <span className="assign-inline-label">Tire ID</span>
                                    <span className="assign-inline-value">{tireToAssign?.name || '—'}</span>
                                </div>
                            </div>

                            <div className="assign-fields-full">
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Asset ID</label>
                                    <select
                                        className="add-tire-select"
                                        value={assignUnitId}
                                        onChange={(e) => handleAssignUnitChange(e.target.value)}
                                    >
                                        <option value="">Select unit...</option>
                                        {unitsWithBlueprints.map((unit) => (
                                            <option key={unit.id} value={unit.id}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="assign-fields-pair">
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Working PSI</label>
                                    <input
                                        className="add-tire-input"
                                        placeholder="Enter PSI"
                                        type="number"
                                        min="0"
                                        value={assignWorkingPSI}
                                        onChange={(e) => setAssignWorkingPSI(e.target.value)}
                                    />
                                </div>
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Remaining Tread Depth %</label>
                                    <div className="assign-readonly-field">
                                        {assignCurrentTread && assignMinTread && parseFloat(assignMinTread) > 0
                                            ? `${Math.round(((parseFloat(assignCurrentTread) - parseFloat(assignMinTread)) / parseFloat(assignCurrentTread)) * 100)}%`
                                            : '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="assign-fields-pair">
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Current Tread Depth</label>
                                    <div className="add-tire-input-with-unit">
                                        <input
                                            className="add-tire-input"
                                            placeholder="e.g. 10"
                                            type="number"
                                            min="0"
                                            max="40"
                                            step="1"
                                            value={assignCurrentTread}
                                            onChange={(e) => setAssignCurrentTread(e.target.value)}
                                        />
                                        <span className="add-tire-unit">/32 in</span>
                                    </div>
                                </div>
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Minimum Tread Depth</label>
                                    <div className="add-tire-input-with-unit">
                                        <input
                                            className="add-tire-input"
                                            placeholder="e.g. 4"
                                            type="number"
                                            min="0"
                                            max="40"
                                            step="1"
                                            value={assignMinTread}
                                            onChange={(e) => setAssignMinTread(e.target.value)}
                                        />
                                        <span className="add-tire-unit">/32 in</span>
                                    </div>
                                </div>
                            </div>

                            <div className="assign-fields-full">
                                <label className="assign-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={assignTreadNotify}
                                        onChange={(e) => setAssignTreadNotify(e.target.checked)}
                                    />
                                    Tread Depth % Email Notification
                                </label>
                            </div>

                            <div className="assign-fields-full">
                                <div className="add-tire-field">
                                    <label className="add-tire-label">Inflation Medium</label>
                                    <SelectWithCustom
                                        value={assignInflationMedium}
                                        onChange={(val) => setAssignInflationMedium(val)}
                                        options={['Compressed air', 'Nitrogen']}
                                        placeholder="Select..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right: Axle diagram */}
                        <div className="assign-right-col">
                            {!assignUnitId && (
                                <p className="assign-placeholder">Select a unit to view axle configuration</p>
                            )}

                            {isLoadingBlueprint && <p className="assign-loading">Loading configuration...</p>}

                            {assignBlueprint && (
                                <>
                                    <div className="assign-config-header">
                                        <span>Axle Configuration</span>
                                        <span className="assign-unit-link">
                                            {unitsWithBlueprints.find((u) => u.id === assignUnitId)?.label}
                                        </span>
                                    </div>

                                    <div className="assign-config-label">
                                        <span className="assign-config-name">{assignBlueprint.name}</span>
                                        <span className="assign-config-desc">
                                            {assignBlueprint.axles.reduce((s, a) => s + a.tireSlots.length, 0)} wheel
                                            positions, {assignBlueprint.axles.length} axle
                                            {assignBlueprint.axles.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    <div className="assign-diagram">
                                        {assignBlueprint.axles.map((axle, axleIdx) => {
                                            const leftSlots = axle.tireSlots
                                                .filter((s) => s.xOffset < 0)
                                                .sort((a, b) => a.xOffset - b.xOffset);
                                            const centerSlots = axle.tireSlots.filter((s) => s.xOffset === 0);
                                            const rightSlots = axle.tireSlots
                                                .filter((s) => s.xOffset > 0)
                                                .sort((a, b) => a.xOffset - b.xOffset);

                                            return (
                                                <div key={axle.id} className="assign-axle-group">
                                                    <div className="assign-axle-row">
                                                        <div className="assign-tire-group left">
                                                            {leftSlots.map((slot) => {
                                                                const occupant = assignUnitTires.find(
                                                                    (t) =>
                                                                        t.assignedPosition === slot.id ||
                                                                        t.assignedPosition === slot.positionCode,
                                                                );
                                                                const isSelected = assignSlotId === slot.id;
                                                                const isEmpty = !occupant;
                                                                return (
                                                                    <div
                                                                        key={slot.id}
                                                                        className={`assign-slot ${isEmpty ? 'empty' : 'occupied'} ${isSelected ? 'selected' : ''}`}
                                                                        onClick={() =>
                                                                            isEmpty && setAssignSlotId(slot.id)
                                                                        }
                                                                        role={isEmpty ? 'button' : undefined}
                                                                        tabIndex={isEmpty ? 0 : undefined}
                                                                    >
                                                                        <div className="assign-slot-box">
                                                                            {isEmpty ? (
                                                                                <span className="assign-slot-plus">
                                                                                    +
                                                                                </span>
                                                                            ) : (
                                                                                <span className="assign-slot-code">
                                                                                    {slot.positionCode}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span
                                                                            className={`assign-slot-label ${isEmpty ? 'action' : ''}`}
                                                                        >
                                                                            {isEmpty ? 'Assign' : occupant?.name || '—'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        <div className="assign-axle-center">
                                                            {centerSlots.map((slot) => {
                                                                const occupant = assignUnitTires.find(
                                                                    (t) =>
                                                                        t.assignedPosition === slot.id ||
                                                                        t.assignedPosition === slot.positionCode,
                                                                );
                                                                const isSelected = assignSlotId === slot.id;
                                                                const isEmpty = !occupant;
                                                                return (
                                                                    <div
                                                                        key={slot.id}
                                                                        className={`assign-slot ${isEmpty ? 'empty' : 'occupied'} ${isSelected ? 'selected' : ''}`}
                                                                        onClick={() =>
                                                                            isEmpty && setAssignSlotId(slot.id)
                                                                        }
                                                                        role={isEmpty ? 'button' : undefined}
                                                                        tabIndex={isEmpty ? 0 : undefined}
                                                                    >
                                                                        <div className="assign-slot-box">
                                                                            {isEmpty ? (
                                                                                <span className="assign-slot-plus">
                                                                                    +
                                                                                </span>
                                                                            ) : (
                                                                                <span className="assign-slot-code">
                                                                                    {slot.positionCode}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span
                                                                            className={`assign-slot-label ${isEmpty ? 'action' : ''}`}
                                                                        >
                                                                            {isEmpty ? 'Assign' : occupant?.name || '—'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                            <div className="assign-axle-line-wrap">
                                                                <div className="assign-axle-line" />
                                                                <span className="assign-axle-badge">{axle.label}</span>
                                                                <div className="assign-axle-line" />
                                                            </div>
                                                        </div>

                                                        <div className="assign-tire-group right">
                                                            {rightSlots.map((slot) => {
                                                                const occupant = assignUnitTires.find(
                                                                    (t) =>
                                                                        t.assignedPosition === slot.id ||
                                                                        t.assignedPosition === slot.positionCode,
                                                                );
                                                                const isSelected = assignSlotId === slot.id;
                                                                const isEmpty = !occupant;
                                                                return (
                                                                    <div
                                                                        key={slot.id}
                                                                        className={`assign-slot ${isEmpty ? 'empty' : 'occupied'} ${isSelected ? 'selected' : ''}`}
                                                                        onClick={() =>
                                                                            isEmpty && setAssignSlotId(slot.id)
                                                                        }
                                                                        role={isEmpty ? 'button' : undefined}
                                                                        tabIndex={isEmpty ? 0 : undefined}
                                                                    >
                                                                        <div className="assign-slot-box">
                                                                            {isEmpty ? (
                                                                                <span className="assign-slot-plus">
                                                                                    +
                                                                                </span>
                                                                            ) : (
                                                                                <span className="assign-slot-code">
                                                                                    {slot.positionCode}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span
                                                                            className={`assign-slot-label ${isEmpty ? 'action' : ''}`}
                                                                        >
                                                                            {isEmpty ? 'Assign' : occupant?.name || '—'}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {axleIdx < assignBlueprint.axles.length - 1 && (
                                                        <div className="assign-shaft" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="assign-footer">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Clear"
                            buttonDisabled={!assignSlotId}
                            buttonOnClick={() => setAssignSlotId('')}
                        />
                        <Btn
                            variant="primary"
                            buttonText={isAssigning ? 'Assigning...' : 'Assign'}
                            buttonDisabled={!assignUnitId || !assignSlotId || isAssigning}
                            buttonOnClick={handleAssignTire}
                        />
                    </div>
                </div>
            </CustomModal>

            {/* Delete Tire Confirmation Modal */}
            <CustomModal
                onDismiss={() => setTireToDelete(null)}
                visible={!!tireToDelete}
                header="Delete Tire"
                footer={
                    <div className="modal-footer-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonDisabled={isDeleting}
                            buttonOnClick={() => setTireToDelete(null)}
                        />
                        <Btn
                            variant="danger"
                            buttonText={isDeleting ? 'Deleting...' : 'Delete Tire'}
                            buttonDisabled={isDeleting}
                            buttonOnClick={handleDeleteTire}
                        />
                    </div>
                }
            >
                <Stack size="m">
                    <div>
                        <strong>Warning:</strong> This action cannot be undone.
                    </div>
                    <div>
                        Are you sure you want to delete the <strong>{tireToDelete?.name}</strong> tire?
                    </div>
                </Stack>
            </CustomModal>

            {/* Unassign Tire Confirmation Modal */}
            <CustomModal
                onDismiss={() => setTireToUnassign(null)}
                visible={!!tireToUnassign}
                header="Remove Tire from Unit"
            >
                <div className="retire-modal-body">
                    <p>
                        This will unassign <strong>{tireToUnassign?.name}</strong> from its current unit and return it
                        to inventory.
                    </p>
                    <p className="retire-note">Tread depth, pressure, and other tire data will be preserved.</p>
                    <div className="retire-modal-footer">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonDisabled={isUnassigning}
                            buttonOnClick={() => setTireToUnassign(null)}
                        />
                        <Btn
                            variant="primary"
                            buttonText={isUnassigning ? 'Removing...' : 'Remove from Unit'}
                            buttonDisabled={isUnassigning}
                            buttonOnClick={handleUnassignTire}
                        />
                    </div>
                </div>
            </CustomModal>

            {/* Remove from Service Modal */}
            <CustomModal
                onDismiss={() => {
                    setTireToRetire(null);
                    setRetireReason('');
                }}
                visible={!!tireToRetire}
                header="Remove from Service"
            >
                <div className="retire-modal-body">
                    <p>
                        This will permanently remove <strong>{tireToRetire?.name}</strong> from active service.
                    </p>
                    <div className="add-tire-field">
                        <label className="add-tire-label">Failure / Removal Reason</label>
                        <textarea
                            className="add-tire-input retire-reason-input"
                            placeholder="e.g. Worn below minimum tread, sidewall damage, etc."
                            value={retireReason}
                            onChange={(e) => setRetireReason(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <div className="retire-modal-footer">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonDisabled={isRetiring}
                            buttonOnClick={() => {
                                setTireToRetire(null);
                                setRetireReason('');
                            }}
                        />
                        <Btn
                            variant="danger"
                            buttonText={isRetiring ? 'Removing...' : 'Remove from Service'}
                            buttonDisabled={isRetiring}
                            buttonOnClick={handleRetireTire}
                        />
                    </div>
                </div>
            </CustomModal>
        </div>
    );
};
